"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";
import { Link } from "@/navigation";
import { cn } from "@/lib/utils";

// 应用项配置接口
interface ApplicationItem {
	titleKey: string;
	descKey: string;
}

// 图片配置接口
interface ImageItem {
	src: string;
	labelKey: string;
	href?: string;
}

// 默认左侧应用列表配置（3项）
export const DEFAULT_LEFT_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app1Title", descKey: "app1Desc" },
	{ titleKey: "app2Title", descKey: "app2Desc" },
	{ titleKey: "app3Title", descKey: "app3Desc" },
];

// 默认中间应用列表配置（3项）
export const DEFAULT_CENTER_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app4Title", descKey: "app4Desc" },
	{ titleKey: "app5Title", descKey: "app5Desc" },
	{ titleKey: "app6Title", descKey: "app6Desc" },
];

// 7项左侧应用配置（header-top 布局左列，4项）
export const FOUR_LEFT_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app1Title", descKey: "app1Desc" },
	{ titleKey: "app2Title", descKey: "app2Desc" },
	{ titleKey: "app3Title", descKey: "app3Desc" },
	{ titleKey: "app4Title", descKey: "app4Desc" },
];

// 7项中间应用配置（header-top 布局中列，3项）
export const THREE_CENTER_APPLICATIONS: ApplicationItem[] = [
	{ titleKey: "app5Title", descKey: "app5Desc" },
	{ titleKey: "app6Title", descKey: "app6Desc" },
	{ titleKey: "app7Title", descKey: "app7Desc" },
];

// 默认右侧图片网格配置
const DEFAULT_IMAGES: ImageItem[] = [
	{
		src: "/image/new-version/machining/five-axis/app/aerospace-components.webp",
		labelKey: "imageLabel1",
		href: "/industry/aerospace",
	},
	{
		src: "/image/new-version/machining/five-axis/app/automotive-motorsport-parts.webp",
		labelKey: "imageLabel2",
		href: "/industry/automotive",
	},
	{
		src: "/image/new-version/machining/five-axis/app/die-mold-tooling.webp",
		labelKey: "imageLabel3",
		href: "/industry/industry-equipment",
	},
	{
		src: "/image/new-version/machining/five-axis/app/medical-implants-devices.webp",
		labelKey: "imageLabel4",
		href: "/industry/medical-device",
	},
	{
		src: "/image/new-version/machining/five-axis/app/marine-naval-components.webp",
		labelKey: "imageLabel5",
		href: "/industry/robotics-automation",
	},
	{
		src: "/image/new-version/machining/five-axis/app/energy-sector-equipment.webp",
		labelKey: "imageLabel6",
		href: "/industry/consumer-electronics",
	},
];

function ApplicationImageCard({ image, label }: { image: ImageItem; label: string }) {
	const card = (
		<>
			{/* 左上角圆形图片 */}
			<div className="relative h-16 w-16 overflow-hidden rounded-full sm:h-20 sm:w-20">
				<Image src={image.src} alt={label} fill className="object-cover" sizes="80px" />
			</div>
			{/* 右下角标签 */}
			<span className="mt-4 self-end text-right text-sm font-semibold text-black transition-colors duration-300 group-hover:text-white group-focus-visible:text-white group-active:text-white sm:text-base">
				{label}
			</span>
		</>
	);

	const className =
		"group flex flex-col rounded-xl bg-[#ededed] p-4 text-black transition-colors duration-300 hover:bg-themePrimary400 hover:text-white focus-visible:bg-themePrimary400 focus-visible:text-white active:bg-themePrimary400 active:text-white";

	if (image.href) {
		return (
			<Link href={image.href} className={className} aria-label={label}>
				{card}
			</Link>
		);
	}

	return <div className={className}>{card}</div>;
}

// 组件 Props 接口
export interface ApplicationProps {
	translationNamespace?: string;
	leftApplications?: ApplicationItem[];
	centerApplications?: ApplicationItem[];
	images?: ImageItem[];
	layout?: "three-column" | "header-top";
	className?: string;
}

export default function Application({
	translationNamespace = "fiveAxisApplication",
	leftApplications = DEFAULT_LEFT_APPLICATIONS,
	centerApplications = DEFAULT_CENTER_APPLICATIONS,
	images = DEFAULT_IMAGES,
	layout = "three-column",
	className,
}: ApplicationProps) {
	const t = useTranslations(translationNamespace);

	// 布局一：三列布局（左列表 + 中列表 + 右图片网格）
	if (layout === "three-column") {
		return (
			<section className={cn("w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
				<div className="container">
					{/* 标题区域 */}
					<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
						{/* 小标题 */}
						<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
							{t("subtitle")}
						</span>

						{/* 主标题 */}
						<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
							{t("title")}
						</h2>

						{/* 描述 */}
						<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
					</div>

					{/* 三列布局 */}
					<div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
						{/* 左侧应用列表 */}
						<div className="space-y-8">
							{leftApplications.map((app, index) => (
								<div key={index}>
									<h3 className="mb-2 text-base font-semibold text-mainColor sm:text-lg">
										{t(app.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(app.descKey)}</p>
								</div>
							))}
						</div>

						{/* 中间应用列表 */}
						<div className="space-y-8">
							{centerApplications.map((app, index) => (
								<div key={index}>
									<h3 className="mb-2 text-base font-semibold text-mainColor sm:text-lg">
										{t(app.titleKey)}
									</h3>
									<p className="text-sm leading-relaxed text-black md:text-base">{t(app.descKey)}</p>
								</div>
							))}
						</div>

						{/* 右侧图片网格 - 卡片式布局 */}
						<div className="grid grid-cols-2 gap-3">
							{images.map((image, index) => (
								<ApplicationImageCard key={index} image={image} label={t(image.labelKey)} />
							))}
						</div>
					</div>
				</div>
			</section>
		);
	}

	// 布局二：标题描述在上，下方三列（左列表 + 中列表 + 右图片网格）
	return (
		<section className={cn("w-full bg-white py-8 sm:py-10 md:py-12 lg:py-16", className)}>
			<div className="container">
				{/* 顶部标题区域 - 单独放上方 */}
				<div className="mb-6 sm:mb-8 md:mb-10 lg:mb-12">
					{/* 小标题 */}
					<span className="mb-3 inline-block text-xl uppercase tracking-wider text-themePrimary400 max-md:text-lg">
						{t("subtitle")}
					</span>

					{/* 主标题 */}
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>

					{/* 描述 */}
					<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
				</div>

				{/* 下方三列布局 */}
				<div className="grid gap-8 lg:grid-cols-3 lg:gap-10">
					{/* 左侧两列文案区域 */}
					{(() => {
						const allApps = [...leftApplications, ...centerApplications];
						const isOdd = allApps.length % 2 !== 0;
						const lastIndex = allApps.length - 1;

						return (
							<div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-2 lg:col-span-2">
								{allApps.map((app, index) => (
									<div key={index} className={isOdd && index === lastIndex ? "md:col-span-2" : ""}>
										<h3 className="mb-2 text-base font-semibold text-themePrimary400 sm:text-lg">
											{t(app.titleKey)}
										</h3>
										<p className="text-sm leading-relaxed text-black md:text-base">{t(app.descKey)}</p>
									</div>
								))}
							</div>
						);
					})()}

					{/* 右侧图片网格 - 卡片式布局 */}
					<div className="grid grid-cols-2 gap-3">
						{images.map((image, index) => (
							<ApplicationImageCard key={index} image={image} label={t(image.labelKey)} />
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
